/** Static source evidence, not a guarantee that every cue is reached in play. */
import { isCueBinding, isSemanticEventName, type CueBinding } from './cueVocabulary.js';

export interface SoundCueCatalogV1 {
  schemaVersion: 1;
  sourceSha256: string;
  names: string[];
}
export interface SoundCueCatalogV2 {
  schemaVersion: 2;
  sourceSha256: string;
  events: Array<{ event: string; cue: CueBinding; staticallyFired: boolean }>;
}
export type SoundCueCatalog = SoundCueCatalogV1 | SoundCueCatalogV2;

export function soundCueEventNames(catalog: SoundCueCatalog): string[] {
  return catalog.schemaVersion === 1 ? catalog.names : catalog.events.map(entry => entry.event);
}

export function readSoundCueCatalog(value: unknown): SoundCueCatalog | null {
  const v = value as SoundCueCatalog;
  if (!v || !/^[0-9a-f]{64}$/.test(v.sourceSha256)) return null;
  if (v.schemaVersion === 1) {
    if (!Array.isArray(v.names) || v.names.length > 64 || new Set(v.names).size !== v.names.length ||
      !v.names.every(isSemanticEventName)) return null;
    return { schemaVersion: 1, sourceSha256: v.sourceSha256, names: [...v.names].sort() };
  }
  if (v.schemaVersion !== 2 || !Array.isArray(v.events) || v.events.length > 64) return null;
  const names = new Set<string>();
  const events: SoundCueCatalogV2['events'] = [];
  for (const entry of v.events) {
    if (!entry || typeof entry !== 'object' || Object.keys(entry).some(key => !['event', 'cue', 'staticallyFired'].includes(key)) ||
      !isSemanticEventName(entry.event) || names.has(entry.event) || !isCueBinding(entry.cue) ||
      typeof entry.staticallyFired !== 'boolean') return null;
    names.add(entry.event);
    events.push({ event: entry.event, cue: entry.cue, staticallyFired: entry.staticallyFired });
  }
  events.sort((a, b) => a.event.localeCompare(b.event));
  return { schemaVersion: 2, sourceSha256: v.sourceSha256, events };
}
